import React from 'react';

import { Box, FormControlLabel, MenuItem, Select, Stack, Switch, Typography } from '@mui/material';

type Option = {
  label: React.ReactNode;
  value: string;
};

type Props = {
  value: Array<string>;
  options: Array<Option>;
  onChange: (value: Array<string>) => void;
  globalLabel: string;
  specificLabel?: string;
  hideWhenGlobal?: boolean;
};

export default function GlobalScopedMultiSelect({
  value,
  options,
  onChange,
  globalLabel,
  specificLabel = 'Specific selections',
  hideWhenGlobal = false,
}: Props) {
  const isGlobal = value.length === 0;

  return (
    <Stack spacing={0.75}>
      <FormControlLabel
        sx={{ m: 0, alignItems: 'flex-start' }}
        control={
          <Switch
            size="small"
            checked={isGlobal}
            onChange={(_, checked) => {
              if (checked) {
                onChange([]);
                return;
              }

              if (value.length === 0 && options.length > 0) {
                onChange([options[0].value]);
              }
            }}
          />
        }
        label={
          <Stack spacing={0.25} sx={{ pt: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {globalLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isGlobal ? 'Applied to every markup' : 'Restricted to selected markups'}
            </Typography>
          </Stack>
        }
      />

      {!isGlobal || !hideWhenGlobal ? (
        <Box>
          {!isGlobal && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {specificLabel}
            </Typography>
          )}
          {!isGlobal || !hideWhenGlobal ? (
            <Select
              multiple
              fullWidth
              size="small"
              value={value}
              onChange={(event) => {
                const nextValue = event.target.value;
                onChange(typeof nextValue === 'string' ? nextValue.split(',') : nextValue);
              }}
              displayEmpty
              renderValue={(selected) => {
                const selectedValues = selected;
                if (!selectedValues.length) {
                  return <Typography color="text.secondary">No specific markups selected</Typography>;
                }

                return selectedValues.join(', ');
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}
